import React, { useState, useEffect, useCallback, useMemo } from "react";
import { CalendarOff, Plus, Loader2, Clock, CheckCircle2, XCircle } from "lucide-react";
import { getMyAbsences, requestAbsence } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { log } from "../../lib/log";

const STATUS = {
  en_attente: { label: "En attente de validation", cls: "bg-amber-100 text-amber-800", icon: Clock },
  approuve: { label: "Validée", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  valide: { label: "Validée", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  refuse: { label: "Refusée", cls: "bg-red-100 text-red-700", icon: XCircle },
};
const fmt = (iso) => new Date(iso + "T12:00").toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
const cls = "w-full border border-neutral-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-brand";

/** App intervenant : déclarer une indisponibilité (validée par un major) + suivi de mes demandes. */
const AbsenceRequest = () => {
  const [data, setData] = useState({ absences: [], types: {} });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ type: "indispo", date_debut: "", date_fin: "", motif: "" });
  const typeOptions = useMemo(() => Object.entries(data.types).filter(([k]) => k !== "autre"), [data.types]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const load = useCallback(() => getMyAbsences().then((r) => setData(r.data)).catch((e) => log.error("absences:", e)), []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await requestAbsence({ ...f, date_fin: f.date_fin || f.date_debut });
      toast({ title: "Demande envoyée ✓", description: "Votre responsable (major) va la valider. Vous serez prévenu par email." });
      setOpen(false); setF({ type: "indispo", date_debut: "", date_fin: "", motif: "" }); load();
    } catch (err) { toast({ title: "Envoi impossible", description: err?.response?.data?.detail || "Erreur", variant: "destructive" }); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-4" data-testid="collab-absences">
      <div className="flex items-center justify-between">
        <p className="font-semibold text-neutral-900 flex items-center gap-2"><CalendarOff size={17} className="text-brand" /> Mes absences</p>
        <button onClick={() => setOpen(!open)} className="text-sm font-semibold text-brand flex items-center gap-1" data-testid="collab-absence-new"><Plus size={15} /> Déclarer</button>
      </div>
      {open && (
        <form onSubmit={submit} className="mt-3 space-y-2" data-testid="collab-absence-form">
          <select className={cls} value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="collab-absence-type">
            {typeOptions.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-neutral-500">Du<input required type="date" className={cls} value={f.date_debut} onChange={(e) => setF({ ...f, date_debut: e.target.value })} data-testid="collab-absence-debut" /></label>
            <label className="text-xs text-neutral-500">Au<input type="date" className={cls} min={f.date_debut} value={f.date_fin} onChange={(e) => setF({ ...f, date_fin: e.target.value })} data-testid="collab-absence-fin" /></label>
          </div>
          <input className={cls} placeholder="Motif (optionnel)" value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })} data-testid="collab-absence-motif" />
          <button type="submit" disabled={loading} className="w-full bg-brand text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60" data-testid="collab-absence-submit">{loading && <Loader2 size={15} className="animate-spin" />} Envoyer la demande</button>
          <p className="text-[11px] text-neutral-400">Vos interventions sur la période seront réaffectées après validation par un major.</p>
        </form>
      )}
      <div className="mt-3 space-y-2">
        {data.absences.length === 0 && !open && <p className="text-sm text-neutral-400">Aucune absence déclarée.</p>}
        {data.absences.slice(0, 5).map((a) => {
          const s = STATUS[a.status] || { label: a.status, cls: "bg-neutral-100 text-neutral-600", icon: Clock };
          return (
            <div key={a.id} className="flex items-center justify-between text-sm" data-testid={`collab-absence-${a.id}`}>
              <span className="text-neutral-700">{a.type_label} · {fmt(a.date_debut)}{a.date_fin !== a.date_debut ? ` → ${fmt(a.date_fin)}` : ""}</span>
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 ${s.cls}`}><s.icon size={11} /> {s.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AbsenceRequest;
